import { formatCurrency as formatMoney } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Search, Download, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const ShiftSearchPage = () => {
  const { api, user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedStore, setSelectedStore] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState(new Set());
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchBusinesses();
    // Set default date range to last 7 days
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    setDateTo(format(today, 'yyyy-MM-dd'));
    setDateFrom(format(weekAgo, 'yyyy-MM-dd'));
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchStores(selectedBusiness);
    } else {
      setStores([]);
    }
  }, [selectedBusiness]);

  const fetchBusinesses = async () => {
    try {
      const res = await api.get('/api-keys/all');
      setBusinesses(res.data);
      if (res.data.length > 0) {
        setSelectedBusiness(res.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load businesses');
    }
  };

  const fetchStores = async (businessId) => {
    setLoadingStores(true);
    try {
      const res = await api.get(`/shifts/stores/${businessId}`);
      setStores(res.data);
    } catch (error) {
      console.error('Failed to load stores');
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedBusiness || !dateFrom || !dateTo) {
      toast.error('Please select a business and date range');
      return;
    }

    setSearching(true);
    setSearchResults(null);
    setSelectedShifts(new Set());

    try {
      const res = await api.post('/shifts/search-loyverse', {
        business_id: selectedBusiness,
        date_from: dateFrom,
        date_to: dateTo,
        store_id: selectedStore === 'all' ? null : selectedStore
      });
      setSearchResults(res.data);
      
      if (res.data.missing > 0) {
        toast.success(`Found ${res.data.missing} missing shift(s)`);
      } else {
        toast.info('All shifts are already imported');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async () => {
    const missingShifts = searchResults?.shifts?.filter(
      s => !s.already_imported && selectedShifts.has(s.loyverse_shift_id)
    );

    if (!missingShifts || missingShifts.length === 0) {
      toast.error('No missing shifts selected');
      return;
    }

    setImporting(true);

    try {
      const res = await api.post('/shifts/import-from-loyverse', {
        business_id: selectedBusiness,
        loyverse_shift_ids: missingShifts.map(s => s.loyverse_shift_id)
      });
      toast.success(res.data.message);
      
      // Refresh search results
      handleSearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const missingIds = searchResults?.shifts
        ?.filter(s => !s.already_imported)
        ?.map(s => s.loyverse_shift_id) || [];
      setSelectedShifts(new Set(missingIds));
    } else {
      setSelectedShifts(new Set());
    }
  };

  const handleSelectOne = (shiftId, checked) => {
    const newSelected = new Set(selectedShifts);
    if (checked) {
      newSelected.add(shiftId);
    } else {
      newSelected.delete(shiftId);
    }
    setSelectedShifts(newSelected);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => formatMoney(amount);

  const missingCount = searchResults?.shifts?.filter(s => !s.already_imported)?.length || 0;
  const selectedMissingCount = searchResults?.shifts?.filter(
    s => !s.already_imported && selectedShifts.has(s.loyverse_shift_id)
  )?.length || 0;

  return (
    <div className="page-container animate-slide-in">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1A1A18]">Search Missing Shifts</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Search Loyverse for shifts that may be missing and import them
        </p>
      </div>

      {/* Search Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5 text-[#006241]" />
            Search Loyverse
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Business</Label>
              <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                <SelectTrigger data-testid="search-business-select">
                  <SelectValue placeholder="Select business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Store (Optional)</Label>
              <Select value={selectedStore} onValueChange={setSelectedStore} disabled={loadingStores}>
                <SelectTrigger data-testid="search-store-select">
                  <SelectValue placeholder={loadingStores ? "Loading..." : "All stores"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                min="2026-01-26"
                data-testid="search-date-from"
              />
            </div>

            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom || "2026-01-26"}
                data-testid="search-date-to"
              />
            </div>
          </div>

          <Button
            onClick={handleSearch}
            disabled={searching || !selectedBusiness || !dateFrom || !dateTo}
            className="mt-4 bg-[#006241] hover:bg-[#004d33]"
            data-testid="search-btn"
          >
            {searching ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Search Loyverse
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Search Results</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {searchResults.business_name} • {searchResults.date_range}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm">
                  <span className="font-medium">{searchResults.total_found}</span> found in Loyverse
                </p>
                <p className="text-sm">
                  <span className="text-green-600 font-medium">{searchResults.already_imported}</span> already imported • 
                  <span className="text-orange-600 font-medium ml-1">{searchResults.missing}</span> missing
                </p>
              </div>
              {selectedMissingCount > 0 && (
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Import {selectedMissingCount} Shift(s)
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {searchResults.shifts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium">No shifts found</h3>
                <p className="text-muted-foreground mt-1">Try adjusting your date range or store filter</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F2F2F0]">
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={missingCount > 0 && selectedMissingCount === missingCount}
                          onCheckedChange={handleSelectAll}
                          disabled={missingCount === 0}
                          data-testid="select-all-missing"
                        />
                      </TableHead>
                      <TableHead>Store / POS</TableHead>
                      <TableHead>Closed At</TableHead>
                      <TableHead className="text-right">Cash</TableHead>
                      <TableHead className="text-right">Card</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.shifts.map((shift) => (
                      <TableRow 
                        key={shift.loyverse_shift_id}
                        className={shift.already_imported ? 'opacity-60' : selectedShifts.has(shift.loyverse_shift_id) ? 'bg-blue-50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedShifts.has(shift.loyverse_shift_id)}
                            onCheckedChange={(checked) => handleSelectOne(shift.loyverse_shift_id, checked)}
                            disabled={shift.already_imported}
                            data-testid={`select-shift-${shift.loyverse_shift_id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{shift.store_name}</p>
                            <p className="text-xs text-muted-foreground">{shift.pos_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{formatDateTime(shift.closed_at)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(shift.cash_payments)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(shift.card_payments)}</TableCell>
                        <TableCell>
                          {shift.already_imported ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {shift.is_reconciled ? 'Reconciled' : 'Imported'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Missing
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ShiftSearchPage;
